import { useEffect, useRef, useState } from "react";
import { Header } from "../components/header";
import { Footer } from "../components/footer";
import { trackEvent, trackPageView } from "../utils/segment";
import { cleanText } from "../utils";

import JollofPlate from "../assets/jollof-plate.png";
import SardinesCan from "../assets/mangrove-sardines-can.png";
import Sardines from "../assets/mangrove-sardines.jpg";
import MackerelCan from "../assets/mangrove-can-mackerel.png";
import Mackerel from "../assets/mangrove-mackerel.jpg";
import SweetCorn from "../assets/mangrove-sweet-corn.jpg";
import GreenPeas from "../assets/mangrove-green-peas.jpg";
import BeansCan from "../assets/mangrove-baked-beans.jpg";
import KidneyBeansCan from "../assets/mangrove-kidney-beans.jpg";
import MangroveFoodsBundle from "../assets/mangrove-foods.jpg";
import MicrowavePrize from "../assets/microwave.png";
import StandingFanPrize from "../assets/standing-fan-img.jpg";
import LeafMotif from "../assets/leaves-right.png";
import ReelThumb1 from "../assets/reel-thumb-1.jpg";
import ReelThumb2 from "../assets/reel-thumb-2.jpg";
import ReelThumb3 from "../assets/reel-thumb-3.jpg";
import ReelThumb4 from "../assets/reel-thumb-4.jpg";
import ReelThumb5 from "../assets/reel-thumb-5.jpg";

const TableSpread = "/imgs/table-food-serving.jpg";

const ENTRY_DEADLINE = new Date("2026-09-20T23:59:59+01:00");

const MANGROVE_SOCIALS = {
  instagram: "https://www.instagram.com/mangrove.foods/",
  tiktok: "https://www.tiktok.com/@mangrovefoods",
  facebook: "https://web.facebook.com/profile.php?id=61578690178560",
  twitter: "https://x.com/MangroveFoods",
};

const GANGSTER_CHEF_INSTAGRAM = "https://www.instagram.com/thegangster_chef";

const ENTRY_FORM_URL = "https://form.typeform.com/to/QClSYiAR";

const GALLERY_REELS = [
  "https://www.instagram.com/reel/Dbv6e5cuddt/",
  "https://www.instagram.com/reel/DbbFgUzIsWQ/",
  "https://www.instagram.com/reel/DbJZG27R_gN/",
  "https://www.instagram.com/reel/DbGjQmUoc3Q/",
  "https://www.instagram.com/reel/Da8crd0OwxF/",
];

function scrollToId(id) {
  document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
}

function useReveal(threshold = 0.15) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (typeof IntersectionObserver === "undefined") {
      setInView(true);
      return;
    }
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          observer.unobserve(el);
        }
      },
      { threshold },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [threshold]);

  return [ref, inView];
}

function Reveal({
  children,
  delay = 0,
  className = "",
  as = "div",
  strong = false,
  ...rest
}) {
  const [ref, inView] = useReveal();
  const Tag = as;
  return (
    <Tag
      ref={ref}
      className={`coc-reveal ${strong ? "coc-reveal-strong" : ""} ${
        inView ? "coc-in-view" : ""
      } ${className}`.trim()}
      style={{ "--coc-delay": `${delay}s` }}
      {...rest}
    >
      {children}
    </Tag>
  );
}

let instagramEmbedScriptPromise = null;

function loadInstagramEmbedScript() {
  if (window.instgrm) return Promise.resolve();
  if (!instagramEmbedScriptPromise) {
    instagramEmbedScriptPromise = new Promise((resolve) => {
      const existing = document.getElementById("coc-ig-embed-script");
      if (existing) {
        existing.addEventListener("load", resolve, { once: true });
        return;
      }
      const script = document.createElement("script");
      script.id = "coc-ig-embed-script";
      script.src = "https://www.instagram.com/embed.js";
      script.async = true;
      script.onload = resolve;
      document.body.appendChild(script);
    });
  }
  return instagramEmbedScriptPromise;
}

function InstagramReelEmbed({ url }) {
  useEffect(() => {
    let cancelled = false;
    loadInstagramEmbedScript().then(() => {
      if (!cancelled) window.instgrm?.Embeds?.process();
    });
    return () => {
      cancelled = true;
    };
  }, [url]);

  return (
    <div className='coc-ig-embed'>
      <blockquote
        className='instagram-media'
        data-instgrm-permalink={url}
        data-instgrm-version='14'
        style={{ background: "#fff", border: 0, margin: 0, width: "100%" }}
      >
        <a href={url} target='_blank' rel='noopener noreferrer'>
          View this reel on Instagram
        </a>
      </blockquote>
    </div>
  );
}

function useCountdown(target) {
  const [timeLeft, setTimeLeft] = useState(() => target - new Date());

  useEffect(() => {
    const id = setInterval(() => setTimeLeft(target - new Date()), 1000);
    return () => clearInterval(id);
  }, [target]);

  const clamped = Math.max(timeLeft, 0);
  return {
    days: Math.floor(clamped / (1000 * 60 * 60 * 24)),
    hours: Math.floor((clamped / (1000 * 60 * 60)) % 24),
    minutes: Math.floor((clamped / (1000 * 60)) % 60),
    seconds: Math.floor((clamped / 1000) % 60),
    expired: clamped <= 0,
  };
}

function pad(n) {
  return String(n).padStart(2, "0");
}

export function CookOffContest() {
  useEffect(() => {
    trackPageView("Landed on the Mangrove Cook-Off Contest Page");
  }, []);

  const countdown = useCountdown(ENTRY_DEADLINE);
  const [acceptedTCs, setAcceptedTCs] = useState(false);
  const [activeReel, setActiveReel] = useState(null);

  useEffect(() => {
    const modalEl = document.getElementById("coc-reel-modal");
    if (!modalEl) return;
    const handleHidden = () => setActiveReel(null);
    modalEl.addEventListener("hidden.bs.modal", handleHidden);
    return () => modalEl.removeEventListener("hidden.bs.modal", handleHidden);
  }, []);

  const handleOpenReel = (url, index) => {
    setActiveReel(url);
    trackEvent("Opened Gallery Reel - Cook Off Contest", { url, index });
  };

  const handleEnterClick = (source) => {
    trackEvent("Clicked Enter Contest", { source });
    scrollToId("enter");
  };

  const openEntryForm = () => {
    trackEvent("Clicked Complete Entry Form - Cook Off Contest");
    const userId = localStorage.getItem("ajs_user_id") ?? "";
    window.open(
      `${ENTRY_FORM_URL}?typeform-source=mangrovefoods.com&userid=${cleanText(
        userId,
      )}`,
      "_blank",
    );
  };

  useEffect(() => {
    if (acceptedTCs) openEntryForm();
    // eslint-disable-next-line
  }, [acceptedTCs]);

  const handleEntryFormClick = () => {
    if (acceptedTCs) {
      openEntryForm();
    } else {
      trackEvent("Opened Terms and Conditions - Cook Off Contest");
      document.getElementById("coc-launch-tcs-button")?.click();
    }
  };

  const handleViewTerms = () => {
    trackEvent("Viewed Terms and Conditions - Cook Off Contest");
    document.getElementById("coc-launch-tcs-button")?.click();
  };

  const products = [
    { name: "Sardines", img: Sardines },
    { name: "Mackerel", img: Mackerel },
    { name: "Baked Beans", img: BeansCan },
    { name: "Sweet Corn", img: SweetCorn },
    { name: "Green Peas", img: GreenPeas },
    { name: "Kidney Beans", img: KidneyBeansCan },
  ];

  const criteria = [
    {
      title: "Creativity and Originality",
      body: "How fresh and original your remix feels.",
      icon: "bi-lightbulb-fill",
    },
    {
      title: "Storytelling",
      body: "How well you bring the dish's story to life.",
      icon: "bi-chat-quote-fill",
    },
    {
      title: "Presentation",
      body: "How the final plate looks and photographs.",
      icon: "bi-camera-fill",
    },
    {
      title: "Effective Use of Mangrove Products",
      body: "How naturally the products fit the dish.",
      icon: "bi-basket2-fill",
    },
    {
      title: "Overall Recipe Execution",
      body: "How well the dish comes together, start to finish.",
      icon: "bi-check-circle-fill",
    },
  ];

  return (
    <div className='coc-page'>
      <Header />

      {/* HERO */}
      <section className='coc-hero'>
        <div className='coc-container coc-hero-grid'>
          <div className='coc-hero-content'>
            <Reveal strong>
              <h1 className='coc-hero-headline'>
                Recreate It.
                <br />
                Remix It.
                <br />
                Make It <em>Mangrove</em>.
              </h1>
            </Reveal>

            <Reveal strong delay={0.16}>
              <p className='coc-hero-subtext'>
                Cook a Gangster Chef recipe with Mangrove products, add your own
                twist, and enter for a chance to win.
              </p>
            </Reveal>

            <Reveal strong delay={0.24} className='coc-hero-actions'>
              <button
                type='button'
                className='coc-btn coc-btn-primary'
                onClick={() => handleEnterClick("hero_primary")}
              >
                Enter the Contest
                <i className='bi bi-arrow-right'></i>
              </button>
              <button
                type='button'
                className='coc-btn coc-btn-dark'
                onClick={() => scrollToId("how-to-enter")}
              >
                How to Enter
              </button>
            </Reveal>
          </div>

          <Reveal strong delay={0.1} className='coc-hero-visual'>
            <img src={LeafMotif} alt='' className='coc-hero-leaf' />
            <span className='coc-hero-ring'></span>

            <div className='coc-hero-frame'>
              <img src={JollofPlate} alt='A plated Mangrove recipe remix' />
            </div>

            <img
              src={SardinesCan}
              alt='Mangrove Sardines'
              className='coc-hero-can coc-can-1'
            />
            <img
              src={MackerelCan}
              alt='Mangrove Mackerel'
              className='coc-hero-can coc-can-2'
            />
          </Reveal>
        </div>
      </section>

      {/* ABOUT / COLLAB */}
      <section className='coc-section coc-about'>
        <div className='coc-container coc-about-grid'>
          <Reveal className='coc-about-media coc-about-media--embed'>
            <InstagramReelEmbed url='https://www.instagram.com/reel/DcDMBRFsENJ/' />
          </Reveal>

          <Reveal delay={0.12} className='coc-about-body'>
            <h2>Cooked Up With The Gangster Chef</h2>
            <p>
              The Gangster Chef built the recipe. Now it's your turn to make it
              yours, one can of Mangrove at a time.
            </p>
            <div className='coc-about-actions'>
              <span className='coc-about-follow-label'>Follow along</span>
              <div className='coc-social-row'>
                <a
                  href={MANGROVE_SOCIALS.instagram}
                  target='_blank'
                  rel='noopener noreferrer'
                  onClick={() => trackEvent("Followed Mangrove Instagram")}
                  aria-label='Mangrove Foods on Instagram'
                >
                  <i className='bi bi-instagram'></i>
                </a>
                <a
                  href={MANGROVE_SOCIALS.tiktok}
                  target='_blank'
                  rel='noopener noreferrer'
                  onClick={() => trackEvent("Followed Mangrove TikTok")}
                  aria-label='Mangrove Foods on TikTok'
                >
                  <i className='bi bi-tiktok'></i>
                </a>
                <a
                  href={MANGROVE_SOCIALS.facebook}
                  target='_blank'
                  rel='noopener noreferrer'
                  onClick={() => trackEvent("Followed Mangrove Facebook")}
                  aria-label='Mangrove Foods on Facebook'
                >
                  <i className='bi bi-facebook'></i>
                </a>
                <a
                  href={MANGROVE_SOCIALS.twitter}
                  target='_blank'
                  rel='noopener noreferrer'
                  onClick={() => trackEvent("Followed Mangrove X")}
                  aria-label='Mangrove Foods on X'
                >
                  <i className='bi bi-twitter-x'></i>
                </a>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* HOW TO ENTER */}
      <section className='coc-section coc-steps' id='how-to-enter'>
        <div className='coc-container'>
          <Reveal as='div' className='coc-section-head'>
            <h2>How to Enter</h2>
            <p>Six simple steps between you and the judges' table.</p>
          </Reveal>

          <div className='coc-steps-rail'>
            <Reveal className='coc-step-row'>
              <span className='coc-step-index'>1</span>
              <div className='coc-step-card'>
                <h3>Follow Both Pages</h3>
                <p>
                  Follow Mangrove Foods and The Gangster Chef so you never miss
                  a judging update.
                </p>
                <div className='coc-chip-row'>
                  <a
                    className='coc-chip'
                    href={MANGROVE_SOCIALS.instagram}
                    target='_blank'
                    rel='noopener noreferrer'
                    onClick={() => trackEvent("Followed Mangrove Instagram")}
                  >
                    <i className='bi bi-instagram'></i>
                    @MangroveFoods
                  </a>
                  <a
                    className='coc-chip'
                    href={GANGSTER_CHEF_INSTAGRAM}
                    target='_blank'
                    rel='noopener noreferrer'
                    onClick={() =>
                      trackEvent("Followed Gangster Chef Instagram")
                    }
                  >
                    <i className='bi bi-instagram'></i>
                    @TheGangsterChef
                  </a>
                </div>
              </div>
            </Reveal>

            <Reveal className='coc-step-row'>
              <span className='coc-step-index'>2</span>
              <div className='coc-step-card'>
                <h3>Recreate a Gangster Chef Recipe</h3>
                <p>
                  Choose any recipe by The Gangster Chef and remake it using at
                  least two Mangrove products.
                </p>
                <div className='coc-chip-row'>
                  {products.map((product) => (
                    <span
                      key={product.name}
                      className='coc-chip coc-chip-static'
                    >
                      {product.name}
                    </span>
                  ))}
                </div>
              </div>
            </Reveal>

            <Reveal className='coc-step-row'>
              <span className='coc-step-index'>3</span>
              <div className='coc-step-card'>
                <h3>Add Your Twist</h3>
                <p>
                  Give the dish your own spin and share the story behind it.
                </p>
              </div>
            </Reveal>

            <Reveal className='coc-step-row'>
              <span className='coc-step-index'>4</span>
              <div className='coc-step-card'>
                <h3>Post Your Entry</h3>
                <p>
                  Share your video or photos on Instagram, TikTok, or Facebook.
                </p>
                <div className='coc-chip-row'>
                  <span className='coc-chip coc-chip-static'>
                    <i className='bi bi-instagram'></i>
                    Instagram
                  </span>
                  <span className='coc-chip coc-chip-static'>
                    <i className='bi bi-tiktok'></i>
                    TikTok
                  </span>
                  <span className='coc-chip coc-chip-static'>
                    <i className='bi bi-facebook'></i>
                    Facebook
                  </span>
                </div>
              </div>
            </Reveal>

            <Reveal className='coc-step-row'>
              <span className='coc-step-index'>5</span>
              <div className='coc-step-card'>
                <h3>Tag and Hashtag</h3>
                <p>Tag both pages, then add the official contest tags.</p>
                <div className='coc-chip-row'>
                  <span className='coc-chip coc-chip-static'>
                    @MangroveFoods
                  </span>
                  <a
                    className='coc-chip'
                    href={GANGSTER_CHEF_INSTAGRAM}
                    target='_blank'
                    rel='noopener noreferrer'
                  >
                    @TheGangsterChef
                  </a>
                  <span className='coc-chip coc-chip-hash'>
                    #MangroveCookOffContest
                  </span>
                  <span className='coc-chip coc-chip-hash'>
                    #MangroveQualityFoods
                  </span>
                </div>
              </div>
            </Reveal>

            <Reveal className='coc-step-row' id='enter'>
              <span className='coc-step-index'>6</span>
              <div className='coc-step-card'>
                <h3>Submit Your Entry</h3>
                <p>
                  Fill out the official entry form and share the link to your
                  post.
                </p>
                <button
                  type='button'
                  className='coc-btn coc-btn-primary coc-step-submit'
                  onClick={handleEntryFormClick}
                >
                  Complete Entry Form
                  <i className='bi bi-send-fill'></i>
                </button>
                <p className='coc-legal-note'>
                  By entering, you agree to the{" "}
                  <button
                    type='button'
                    className='coc-inline-link'
                    onClick={handleViewTerms}
                  >
                    Terms and Conditions
                  </button>
                  .
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* GET INSPIRED */}
      <section className='coc-section coc-gallery'>
        <div className='coc-container'>
          <Reveal as='div' className='coc-section-head'>
            <h2>Every Plate Tells a Story</h2>
            <p>A little inspiration before you start remixing.</p>
          </Reveal>

          <div className='coc-gallery-grid'>
            {[
              {
                className: "coc-tile-a",
                img: ReelThumb1,
                alt: "Instagram reel cover: Mangrove sardines baked with tomatoes",
              },
              {
                className: "coc-tile-b",
                img: ReelThumb2,
                alt: "Instagram reel cover: rice bowl with Mangrove sardines and chili crunch",
              },
              {
                className: "coc-tile-c",
                img: ReelThumb3,
                alt: "Instagram reel cover: a Mangrove remix recipe",
              },
              {
                className: "coc-tile-d",
                img: ReelThumb4,
                alt: "Instagram reel cover: a Mangrove remix recipe",
              },
              {
                className: "coc-tile-e",
                img: ReelThumb5,
                alt: "Instagram reel cover: a Mangrove remix recipe",
              },
            ].map((tile, i) => (
              <Reveal
                key={tile.className}
                delay={i * 0.06}
                as='div'
                className={`coc-gallery-tile ${tile.className}`}
              >
                <button
                  type='button'
                  className='coc-gallery-play'
                  data-bs-toggle='modal'
                  data-bs-target='#coc-reel-modal'
                  onClick={() => handleOpenReel(GALLERY_REELS[i], i)}
                  aria-label={`Play Instagram reel: ${tile.alt}`}
                >
                  <img src={tile.img} alt={tile.alt} />
                  <span className='coc-gallery-play-scrim'></span>
                  <span className='coc-gallery-play-icon'>
                    <i className='bi bi-play-fill'></i>
                  </span>
                </button>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ELIGIBLE PRODUCTS */}
      <section className='coc-section coc-products' id='products'>
        <div className='coc-container'>
          <Reveal as='div' className='coc-section-head'>
            <h2>Six Products. Endless Plates.</h2>
            <p>Use at least two of these in your remix.</p>
          </Reveal>

          <div className='coc-product-grid'>
            {products.map((product, i) => (
              <Reveal
                key={product.name}
                as='div'
                delay={i * 0.05}
                className='coc-product-card'
              >
                <div
                  className={`coc-product-media ${
                    product.img ? "" : "coc-product-flat"
                  }`}
                >
                  {product.img ? (
                    <img src={product.img} alt={product.name} />
                  ) : (
                    <i className='bi bi-basket2-fill'></i>
                  )}
                </div>
                <div className='coc-product-body'>
                  <h3>{product.name}</h3>
                  <span className='coc-product-tag'>Eligible</span>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* JUDGING CRITERIA */}
      <section className='coc-section coc-judging' id='judging'>
        <div className='coc-container'>
          <Reveal as='div' className='coc-section-head'>
            <h2>How We Pick Winners</h2>
            <p>Every entry is scored against the same five measures.</p>
          </Reveal>

          <div className='coc-criteria-list'>
            {criteria.map((item, i) => (
              <Reveal
                key={item.title}
                delay={i * 0.04}
                as='div'
                className='coc-criteria-row'
              >
                <i className={`bi ${item.icon} coc-criteria-icon`}></i>
                <div>
                  <h3>{item.title}</h3>
                  <p>{item.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* PRIZES */}
      <section className='coc-section coc-prizes' id='prizes'>
        <div className='coc-container'>
          <Reveal as='div' className='coc-section-head'>
            <h2>What You Could Win</h2>
            <p>Three winners walk away with prizes built for the kitchen.</p>
          </Reveal>

          <div className='coc-prize-grid'>
            <Reveal as='div' className='coc-prize-card coc-prize-first'>
              <img
                src={MicrowavePrize}
                alt='Mangrove branded microwave'
                className='coc-prize-photo'
              />
              <span className='coc-prize-scrim'></span>
              <span className='coc-prize-rank'>
                <i className='bi bi-trophy-fill'></i>
                1st Place
              </span>
              <h3>Mangrove Branded Microwave</h3>
              <p>Plus a Mangrove Product Bundle</p>
            </Reveal>

            <Reveal
              delay={0.1}
              as='div'
              className='coc-prize-card coc-prize-second'
            >
              <img
                src={StandingFanPrize}
                alt='Standing fan'
                className='coc-prize-photo'
              />
              <span className='coc-prize-scrim'></span>
              <span className='coc-prize-rank'>
                <i className='bi bi-award-fill'></i>
                2nd Place
              </span>
              <h3>Standing Fan</h3>
              <p>Plus a Mangrove Product Bundle</p>
            </Reveal>

            <Reveal
              delay={0.18}
              as='div'
              className='coc-prize-card coc-prize-third'
            >
              <img
                src={MangroveFoodsBundle}
                alt='The full Mangrove product lineup'
                className='coc-prize-photo'
              />
              <span className='coc-prize-scrim'></span>
              <span className='coc-prize-rank'>
                <i className='bi bi-gift-fill'></i>
                3rd Place
              </span>
              <h3>Mangrove Product Bundle</h3>
            </Reveal>
          </div>
        </div>
      </section>

      {/* KEY DATES */}
      <section className='coc-section coc-dates' id='dates'>
        <div className='coc-dates-media'>
          <img src={TableSpread} alt='' />
          <span className='coc-dates-scrim'></span>
        </div>
        <div className='coc-dates-inner'>
          <div className='coc-container'>
            <Reveal as='div' className='coc-section-head'>
              <h2>Key Dates</h2>
              <p>Mark your calendar and cook with time to spare.</p>
            </Reveal>

            <Reveal as='div' className='coc-dates-rail'>
              <div className='coc-date-stop'>
                <div className='coc-date-value'>Aug 14</div>
                <div className='coc-date-label'>Contest opens</div>
              </div>
              <div className='coc-date-stop'>
                <div className='coc-date-value'>Sep 20</div>
                <div className='coc-date-label'>
                  Entries close, 11:59 PM WAT
                </div>
              </div>
              <div className='coc-date-stop coc-date-muted'>
                <div className='coc-date-value'>Sep 21 to 24</div>
                <div className='coc-date-label'>Judging period</div>
              </div>
              <div className='coc-date-stop coc-date-muted'>
                <div className='coc-date-value'>Sep 25</div>
                <div className='coc-date-label'>Winners announced</div>
              </div>
            </Reveal>

            <Reveal delay={0.1} as='div' className='coc-countdown'>
              <span className='coc-countdown-label'>
                {countdown.expired
                  ? "Entries are now closed."
                  : "Time left to submit your entry"}
              </span>
              <div className='coc-countdown-clock'>
                <div className='coc-countdown-unit'>
                  <div className='coc-countdown-num'>{pad(countdown.days)}</div>
                  <div className='coc-countdown-unit-label'>Days</div>
                </div>
                <div className='coc-countdown-unit'>
                  <div className='coc-countdown-num'>
                    {pad(countdown.hours)}
                  </div>
                  <div className='coc-countdown-unit-label'>Hrs</div>
                </div>
                <div className='coc-countdown-unit'>
                  <div className='coc-countdown-num'>
                    {pad(countdown.minutes)}
                  </div>
                  <div className='coc-countdown-unit-label'>Min</div>
                </div>
                <div className='coc-countdown-unit'>
                  <div className='coc-countdown-num'>
                    {pad(countdown.seconds)}
                  </div>
                  <div className='coc-countdown-unit-label'>Sec</div>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className='coc-final'>
        <Reveal className='coc-container'>
          <h2>Recreate It. Remix It. Make It Mangrove.</h2>
          <p>Ready to show us your Mangrove Standard?</p>
          <div className='coc-final-actions'>
            <button
              type='button'
              className='coc-btn coc-btn-on-dark'
              onClick={() => handleEnterClick("final_cta")}
            >
              Enter the Contest
              <i className='bi bi-arrow-right'></i>
            </button>
          </div>
          <div className='coc-final-hashtags'>
            <span className='coc-chip'>#MangroveCookOffContest</span>
            <span className='coc-chip'>#MangroveQualityFoods</span>
          </div>
        </Reveal>
      </section>

      <Footer />

      <button
        type='button'
        className='btn btn-primary'
        style={{ display: "none" }}
        data-bs-toggle='modal'
        data-bs-target='#coc-tcs-modal'
        id='coc-launch-tcs-button'
      >
        Launch Terms and Conditions modal
      </button>

      <TermsAndConditionsModal
        onAccept={() => {
          setAcceptedTCs(true);
          trackEvent("Accepted Terms and Conditions - Cook Off Contest");
        }}
      />

      <div
        className='modal fade'
        id='coc-reel-modal'
        tabIndex='-1'
        role='dialog'
        aria-hidden='true'
        aria-label='Instagram reel'
      >
        <div className='modal-dialog modal-dialog-centered' role='document'>
          <div className='modal-content coc-reel-modal-content'>
            <button
              type='button'
              className='coc-reel-modal-close'
              data-bs-dismiss='modal'
              aria-label='Close'
            >
              <i className='bi bi-x-lg'></i>
            </button>
            <div className='modal-body coc-reel-modal-body'>
              {activeReel && <InstagramReelEmbed url={activeReel} />}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function TermsAndConditionsModal({ onAccept }) {
  return (
    <div
      className='modal fade'
      tabIndex='-1'
      role='dialog'
      aria-hidden='true'
      id='coc-tcs-modal'
      data-bs-backdrop='modal'
      aria-labelledby='coc-tcs-modal-label'
    >
      <div
        className='modal-dialog modal-dialog-centered modal-dialog-scrollable'
        role='document'
      >
        <div className='modal-content'>
          <div className='modal-header'>
            <h3 className='modal-title' id='coc-tcs-modal-label'>
              Mangrove Cook-Off Contest Terms and Conditions
            </h3>
          </div>
          <div className='modal-body'>
            <h4>1. Promoter</h4>
            <p>
              The Mangrove Cook-Off Contest ("the Contest") is organized by
              Mangrove Quality Foods in partnership with The Gangster Chef.
            </p>

            <h4>2. Eligibility</h4>
            <ul>
              <li>
                The Contest is open to residents of Nigeria aged 18 years and
                above.
              </li>
              <li>
                Employees of Mangrove Quality Foods, their immediate family
                members, agencies, and partners directly involved in the
                administration of the Contest are not eligible to participate.
              </li>
              <li>
                Entry is free. However, participants are responsible for any
                costs incurred in creating and posting their entries.
              </li>
            </ul>

            <h4>3. Contest Period</h4>
            <p>
              The Contest opens on August 14, 2026 and closes on September 20,
              2026 at 11:59 PM WAT.
              <br />
              Entries submitted after this deadline will not be considered.
            </p>

            <h4>4. Entry Requirements</h4>
            <p>To enter, participants must:</p>
            <ul>
              <li>
                Follow Mangrove Quality Foods and The Gangster Chef on social
                media.
              </li>
              <li>
                Recreate any recipe previously created by The Gangster Chef.
              </li>
              <li>Use at least two (2) Mangrove products in their recipe.</li>
              <li>Add a creative twist and storytelling element.</li>
              <li>Post their entry on Instagram, TikTok, or Facebook.</li>
              <li>Tag Mangrove Foods and The Gangster Chef.</li>
              <li>
                Use the hashtags #MangroveCookOffContest and
                #MangroveQualityFoods.
              </li>
              <li>
                Complete and submit the official entry form with a valid link to
                their entry.
              </li>
            </ul>
            <p>
              Failure to meet any of the above requirements may result in
              disqualification.
            </p>

            <h4>5. Judging</h4>
            <p>
              Entries will be reviewed by a panel comprising representatives of
              Mangrove Quality Foods and The Gangster Chef. Entries will be
              scored based on:
            </p>
            <ul>
              <li>Creativity and Originality</li>
              <li>Storytelling</li>
              <li>Presentation</li>
              <li>Effective Use of Mangrove Products</li>
              <li>Overall Recipe Execution</li>
            </ul>
            <p>The judges' decision shall be final.</p>

            <h4>6. Prizes</h4>
            <ul>
              <li>
                <b>First Place:</b> Mangrove Branded Microwave and a Mangrove
                Product Bundle
              </li>
              <li>
                <b>Second Place:</b> Standing Fan and a Mangrove Product Bundle
              </li>
              <li>
                <b>Third Place:</b> Mangrove Product Bundle
              </li>
            </ul>
            <p>Prizes are non-transferable and cannot be exchanged for cash.</p>

            <h4>7. Winner Notification</h4>
            <p>
              Winners will be contacted via the details provided in the entry
              form and announced on Mangrove Foods' social media channels.
              <br />
              If a winner cannot be reached within seven (7) days of
              notification, Mangrove Foods reserves the right to select an
              alternate winner.
            </p>

            <h4>8. Content Usage Rights</h4>
            <p>
              By entering the Contest, participants grant Mangrove Quality Foods
              a non-exclusive, royalty-free right to repost, share, publish, and
              use submitted content for promotional and marketing purposes
              across its owned communication channels, with appropriate credit
              where applicable.
            </p>

            <h4>9. Prize Fulfilment</h4>
            <p>
              Prizes will be delivered to winners within Nigeria. Delivery
              timelines may vary depending on the winner's location and
              logistical considerations.
              <br />
              Mangrove Foods shall not be liable for delays caused by
              third-party logistics providers or circumstances beyond its
              reasonable control.
            </p>

            <h4>10. General</h4>
            <p>
              Mangrove Foods reserves the right to amend, suspend, or cancel the
              Contest if circumstances arise that make such action necessary.
              <br />
              Participation in the Contest constitutes acceptance of these Terms
              and Conditions.
            </p>
          </div>
          <div className='modal-footer'>
            <button
              type='button'
              data-bs-dismiss='modal'
              className='btn btn-danger'
            >
              Close
            </button>
            <button
              type='button'
              className='btn btn-primary'
              data-bs-dismiss='modal'
              onClick={onAccept}
            >
              Accept Terms and Conditions
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
